import React, { useState, useMemo, useEffect } from "react";
import { PositionView } from "./PositionView";
import { OrdersView } from "./OrdersView";
import { AccountView } from "./AccountView";
import { RawPositionsView } from "./RawPositionsView";
import { BrokerRawPositionsView } from "./BrokerRawPositionsView";
import { BacktestView } from "./BacktestView";

import { usePositionsView } from "../hooks/usePositionsView";
import { useOrdersView } from "../hooks/useOrdersView";
import { useAccountView } from "../hooks/useAccountView";

import { usePositionStore } from "../contexts/LTPStore/usePositionsStore";
import { useOrderStore } from "../contexts/LTPStore/useOrdersStore";
import { useRawPositionStore } from "../contexts/LTPStore/useRawPositionStore";
import { useGlobalUserMappings } from "../hooks/useGlobalHooks";
import RejectedOrderView from "../components/RejectedOrderView"
import { useRejectedOrdersView } from "../hooks/useRejectedOrdersView";
import { useCommoditiesView } from "../hooks/useCommoditiesView";
import { usePledgePlusOrdersView } from "../hooks/usePledgePlusOrdersView";
import { useAlpacaPositionsView } from "../hooks/useAlpacaPositionsView";
import { useAlpacaOrdersView } from "../hooks/useAlpacaOrdersView";
import { useStockOptionsPositionsView, useStockOptionsOrdersView, getStockFromSymbol, isIndexSymbol } from "../hooks/useStockOptionsView";
import { useCommodityOptionsPositionsView, useCommodityOptionsOrdersView, getCommodityOptionFromSymbol } from "../hooks/useCommodityOptionsView";
import MiniDropdownTab from "../components/DashboardNewComponents/MiniDropdownTab"
import { ExitRawPositionsView } from "./ExitRawPositionsView";
import { CommoditiesPositionsView } from "./CommoditiesPositionsView";
import { PledgePlusOrdersView } from "./PledgePlusOrdersView";
import { useRejectedOrderStore } from "../contexts/LTPStore/useRejectedOrderStore";
import { AlpacaPositionsView } from "./AlpacaPositionsView";
import { AlpacaOrdersView } from "./AlpacaOrdersView";
import { RmsArbPositionsView } from "./RmsArbPositionsView";
import { RmsArbOrdersView } from "./RmsArbOrdersView";
import { AlertCircle, Settings, RefreshCw, Activity, Wifi, WifiOff, Zap, Bell, Radio } from 'lucide-react';
import apiClient from "../utils/apiClient";
import server from "../utils/serverConfig";
import { useGlobalData } from "../hooks/useGlobalHooks";
import { useWebSocketStatusStore } from "../contexts/LTPStore/useWebSocketStatusStore";
import WebSocketManager from "../contexts/websocketManager";
// import { MarketClock } from "./DashboardNewComponents/MarketClock";
// import { NudgeManager } from "./DashboardNewComponents/NudgeManager";
import SignalToastOverlay from "./SignalToastOverlay";
import GapPlaybookView from "./GapPlaybookView";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../Login/firebase";

export function DashboardNew() {
  const { refreshAll, pingWebSocket, reconnectWebSocket, users, multiAssetUnderlyings } = useGlobalData();
  const wsStatus = useWebSocketStatusStore((s) => s.status);

  // MarketClock handles its own interval rendering

  const handleReconnect = () => {
    reconnectWebSocket();
    WebSocketManager.getInstance().reconnectWebSocket();
  };

  const handlePing = () => {
    pingWebSocket();
    WebSocketManager.getInstance().pingWebSocket();
  };

  // -------------------------------------------------------
  // 1️⃣ POSITIONS from Zustand
  // -------------------------------------------------------
  const positionsRaw = usePositionStore((s) => s.positions);
  const positionsVersion = usePositionStore((s) => s.version);  // 🔥 new version
  const positionsLoading = usePositionStore((s) => s.loading ?? false);
  // console.log("RE RENDERING--------");
  // ensure array format
  const positionsArray = Array.isArray(positionsRaw)
    ? positionsRaw
    : positionsRaw && typeof positionsRaw === "object"
      ? Object.values(positionsRaw)
      : [];

  const rawBrokerPositions = useRawPositionStore((s) => s.rawPositions) || [];


  // -------------------------------------------------------
  // 2️⃣ ORDERS from Zustand
  // -------------------------------------------------------
  const orders = useOrderStore((s) => s.orders) || [];
  const RejectedOrder = useRejectedOrderStore((s) => s.orders) || [];
  const rejectOrdersLoading = useRejectedOrderStore((s) => s.loading ?? false);
  const ordersUpdateVersion = useOrderStore((s) => s.updateVersion ?? 0);
  const ordersLoading = useOrderStore((s) => s.loading ?? false);

  // console.log("Orders HERE I GOT_------",orders)
  // -------------------------------------------------------
  // 3️⃣ USER MAPPINGS
  // -------------------------------------------------------
  const { userMappings, loading: userMappingsLoading } =
    useGlobalUserMappings();



  const safeUserMappings = Array.isArray(userMappings) ? userMappings : [];

  // -------------------------------------------------------
  // 4️⃣ PROCESS POSITION + ORDER VIEWS
  // -------------------------------------------------------
  const positionViewData = usePositionsView(
    positionsArray,
    orders,
    safeUserMappings,
    positionsVersion,
    multiAssetUnderlyings,
    users
  );

  const orderViewData = useOrdersView(
    orders,
    safeUserMappings,
    ordersUpdateVersion,
    users,
    multiAssetUnderlyings
  );



  const rejectedOrdersData = useRejectedOrdersView(
    RejectedOrder,
    safeUserMappings,
    ordersUpdateVersion,
  );

  const commoditiesViewData = useCommoditiesView(
    positionsArray,
    orders,
    safeUserMappings,
    positionsVersion
  );

  const pledgePlusOrdersViewData = usePledgePlusOrdersView(
    orders,
    safeUserMappings,
    positionsArray
  );

  const stockOptionsPositionsData = useStockOptionsPositionsView(
    positionsArray,
    orders,
    safeUserMappings,
    positionsVersion
  );

  const stockOptionsOrdersData = useStockOptionsOrdersView(
    orders,
    safeUserMappings,
    ordersUpdateVersion,
    users
  );

  const commodityOptionsPositionsData = useCommodityOptionsPositionsView(
    positionsArray,
    orders,
    safeUserMappings,
    positionsVersion
  );

  const commodityOptionsOrdersData = useCommodityOptionsOrdersView(
    orders,
    safeUserMappings,
    ordersUpdateVersion,
    users
  );

  const accountViewData = useAccountView();

  // console.log("ORDER DATA OUT0000ER_----",accountViewData);
  // -------------------------------------------------------
  // 5️⃣ LOADING + COUNTS
  // -------------------------------------------------------
  const isPositionsInitialLoading =
    positionsLoading && positionsArray.length === 0;

  const isOrdersInitialLoading =
    ordersLoading && orders.length === 0;

  const indexPositionsCount =
    positionsArray.filter((p) => {
      const s = (p.symbol || "").toUpperCase();

      // 1. Exclude MCX and futures (FUT)
      if (s.includes("MCX") || s.endsWith("FUT")) return false;

      // 2. Exclude closed positions (qty 0)
      const netQty = parseFloat(p.net_qty ?? p.qty ?? 0);
      if (Math.abs(netQty) === 0) return false;

      // 3. Include only Index options
      return isIndexSymbol(s);
    }).length || 0;

  const stockOptionsPositionsCount = useMemo(() => {
    return positionsArray.filter((p) => {
      const clientId = p.client_id || p.clientId;
      if (clientId !== "CA00246") return false;
      const stock = getStockFromSymbol(p.symbol);
      const netQty = parseFloat(p.net_qty ?? p.qty ?? 0);
      return stock !== null && Math.abs(netQty) > 0;
    }).length || 0;
  }, [positionsArray]);

  const commodityOptionsPositionsCount = useMemo(() => {
    return positionsArray.filter((p) => {
      const clientId = p.client_id || p.clientId;
      if (clientId !== "CA00246") return false;
      const comm = getCommodityOptionFromSymbol(p.symbol);
      const netQty = parseFloat(p.net_qty ?? p.qty ?? 0);
      return comm !== null && Math.abs(netQty) > 0;
    }).length || 0;
  }, [positionsArray]);

  const indexOptionsTotalCount = indexPositionsCount + (orderViewData.buyOrdersCount || 0);
  const stockOptionsTotalCount = stockOptionsPositionsCount + (stockOptionsOrdersData.buyOrdersCount || 0);
  const commodityOptionsTotalCount = commodityOptionsPositionsCount + (commodityOptionsOrdersData.buyOrdersCount || 0);
  const optimusTotalCount = indexOptionsTotalCount + stockOptionsTotalCount + commodityOptionsTotalCount;

  const indexOrders = useMemo(() => {
    return (orders || []).filter((o) => isIndexSymbol(o.symbol));
  }, [orders]);

  const stockOrders = useMemo(() => {
    return (orders || []).filter((o) => getStockFromSymbol(o.symbol) !== null);
  }, [orders]);

  const commodityOrders = useMemo(() => {
    return (orders || []).filter((o) => getCommodityOptionFromSymbol(o.symbol) !== null);
  }, [orders]);

  const alpacaPositionsViewData = useAlpacaPositionsView(
    positionsArray,
    orders,
    users
  );

  const alpacaOrdersViewData = useAlpacaOrdersView(
    orders,
    users,
    positionsArray
  );

  const alpacaPositionsCount = useMemo(() => {
    return alpacaPositionsViewData?.data?.length || 0;
  }, [alpacaPositionsViewData]);

  const alpacaOrdersCount = useMemo(() => {
    return alpacaOrdersViewData?.data?.length || 0;
  }, [alpacaOrdersViewData]);


  const hasNegativeSell = positionsArray.some(p => {
    const side = (p.side === 1 || p.side === '1' || String(p.side).toLowerCase().startsWith('b')) ? 'BUY' :
      (p.side === 2 || p.side === -1 || p.side === '2' || String(p.side).toLowerCase().startsWith('s')) ? 'SELL' : '';
    const qty = parseFloat(p.net_qty ?? p.qty ?? 0);
    const symbol = (p.symbol || "").toUpperCase();
    return side === 'SELL' && qty < 0 && !symbol.startsWith("MCX") && !symbol.endsWith("FUT");
  });

  // console.log("Over view data---",orderViewData);



  const [showSettings, setShowSettings] = useState(false);
  const [showGapModal, setShowGapModal] = useState(false);
  const [gapNotification, setGapNotification] = useState(false);

  const fetchGapResultsBackground = async () => {
    try {
      const response = await apiClient.get("gap-playbook/results");
      if (response.data && response.data.status === "success") {
        const hasData = response.data.data && (
          (response.data.data.optimus && response.data.data.optimus.length > 0) ||
          (response.data.data.pledgeplus && response.data.data.pledgeplus.length > 0)
        );
        if (hasData) {
          setGapNotification(true);
        }
      }
    } catch (err) {
      console.error("Error fetching gap playbook results background:", err);
    }
  };

  React.useEffect(() => {
    // Initial fetch on mount
    fetchGapResultsBackground();

    const interval = setInterval(() => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();

      // Check if it is exactly 9:01 AM or 9:16 AM local time
      if ((hours === 9 && minutes === 1) || (hours === 9 && minutes === 16)) {
        console.log(`Triggering scheduled gap results fetch at ${hours}:${minutes}`);
        fetchGapResultsBackground();
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  const [sellModule, setSellModule] = useState(1);
  const [exitModule, setExitModule] = useState(1);
  const [sellXtsEnabled, setSellXtsEnabled] = useState(true); // true = ON, false = OFF
  const [sellXtsAnuragEnabled, setSellXtsAnuragEnabled] = useState(true); // true = ON, false = OFF
  const [sellFyersEnabled, setSellFyersEnabled] = useState(true); // true = ON, false = OFF
  const [sellDhanHqEnabled, setSellDhanHqEnabled] = useState(true); // true = ON, false = OFF
  const [sellNubraEnabled, setSellNubraEnabled] = useState(true); // true = ON, false = OFF
  const [sellPledgePlusDhanHqEnabled, setSellPledgePlusDhanHqEnabled] = useState(true);
  const [sellPledgePlusFyersEnabled, setSellPledgePlusFyersEnabled] = useState(true);
  const [sellPledgePlusZerodhaEnabled, setSellPledgePlusZerodhaEnabled] = useState(true);
  const [sellPledgePlusXtsEnabled, setSellPledgePlusXtsEnabled] = useState(true);
  const [sellPledgePlusXtsAnuragEnabled, setSellPledgePlusXtsAnuragEnabled] = useState(true);
  const [pledgeIndexFyersEnabled, setPledgeIndexFyersEnabled] = useState(true);
  const [pledgeIndexDhanHqEnabled, setPledgeIndexDhanHqEnabled] = useState(true);
  const [pledgeIndexZerodhaEnabled, setPledgeIndexZerodhaEnabled] = useState(true);

  // Fetch initial settings from backend (optional but recommended)
  React.useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await apiClient.get(`${server.godseye.baseUrl}optimus/settings`);
        setSellModule(parseInt(res.data.data.sell_module ?? 1));
        setExitModule(parseInt(res.data.data.exit_module ?? 1));
      } catch (err) {
        console.error("Settings fetch failed", err);
      }

      // Fetch Sell XTS (Optimus) status from Optimus server
      try {
        const xtsRes = await apiClient.get("https://optimus.dhanss.server.sypto.xyz/optimus/settings/status/sell_xts");
        setSellXtsEnabled(xtsRes.data.enabled ?? true);
      } catch (err) {
        console.error("Sell XTS status fetch failed", err);
        // Defaults to true (enabled) if fetch fails — safe fallback
      }

      // Fetch Sell XTS Anurag (Optimus) status from Optimus Anurag server
      try {
        const xtsResAnurag = await apiClient.get("https://optimus.dhanss.anurag.server.sypto.xyz/optimus/settings/status/sell_xts");
        setSellXtsAnuragEnabled(xtsResAnurag.data.enabled ?? true);
      } catch (err) {
        console.error("Sell XTS Anurag status fetch failed", err);
        // Defaults to true (enabled) if fetch fails
      }

      // Fetch Sell Fyers (Optimus) status from Godseye server
      try {
        const fyersRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_fyers");
        setSellFyersEnabled(fyersRes.data.enabled ?? true);
      } catch (err) {
        console.error("Sell Fyers status fetch failed", err);
        // Defaults to true (enabled) if fetch fails
      }

      // Fetch Sell DhanHQ (Optimus) status from Godseye server
      try {
        const dhanHqRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_dhanhq");
        setSellDhanHqEnabled(dhanHqRes.data.enabled ?? true);
      } catch (err) {
        console.error("Sell DhanHQ status fetch failed", err);
      }

      // Fetch Sell Nubra status from Godseye server
      try {
        const nubraRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_nubra");
        setSellNubraEnabled(nubraRes.data.enabled ?? true);
      } catch (err) {
        console.error("Sell Nubra status fetch failed", err);
      }

      // Fetch Pledge Plus sell statuses
      try {
        const ppDhanRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_dhanhq");
        setSellPledgePlusDhanHqEnabled(ppDhanRes.data.enabled ?? true);
      } catch (err) { console.error("Sell Pledge Plus DhanHQ status fetch failed", err); }
      try {
        const ppFyersRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_fyers");
        setSellPledgePlusFyersEnabled(ppFyersRes.data.enabled ?? true);
      } catch (err) { console.error("Sell Pledge Plus Fyers status fetch failed", err); }
      try {
        const ppZerodhaRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_zerodha");
        setSellPledgePlusZerodhaEnabled(ppZerodhaRes.data.enabled ?? true);
      } catch (err) { console.error("Sell Pledge Plus Zerodha status fetch failed", err); }
      try {
        const ppxRes = await apiClient.get("https://optimus.dhanss.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_dhan_xts");
        setSellPledgePlusXtsEnabled(ppxRes.data.enabled ?? true);
      } catch (err) { console.error("Sell Pledge Plus XTS status fetch failed", err); }
      try {
        const ppxResAnurag = await apiClient.get("https://optimus.dhanss.anurag.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_dhan_xts");
        setSellPledgePlusXtsAnuragEnabled(ppxResAnurag.data.enabled ?? true);
      } catch (err) { console.error("Sell Pledge Plus XTS Anurag status fetch failed", err); }

      // Fetch Pledge+ Index module statuses
      try {
        const piiFyersRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/pledge_sl_index_module_fyers");
        setPledgeIndexFyersEnabled(piiFyersRes.data.enabled ?? true);
      } catch (err) { console.error("Pledge Index Fyers status fetch failed", err); }
      try {
        const piiDhanRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/pledge_sl_index_module_dhanhq");
        setPledgeIndexDhanHqEnabled(piiDhanRes.data.enabled ?? true);
      } catch (err) { console.error("Pledge Index DhanHQ status fetch failed", err); }
      try {
        const piiZerodhaRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/pledge_sl_index_module_zerodha");
        setPledgeIndexZerodhaEnabled(piiZerodhaRes.data.enabled ?? true);
      } catch (err) { console.error("Pledge Index Zerodha status fetch failed", err); }
    };

    loadSettings();
  }, []);


  // NudgeManager handles nudge states

  // -------------------------------------------------------
  // 🔔 SIGNAL UPDATE ALERT
  // -------------------------------------------------------
  const [signalAlertEnabled, setSignalAlertEnabled] = useState(() => {
    try { return localStorage.getItem("signalAlertEnabled") === "true"; }
    catch { return false; }
  });

  console.log("TIcK TICK");
  const handleToggleSignalAlert = () => {
    setSignalAlertEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem("signalAlertEnabled", String(next)); } catch { }
      if (next) {
        // Fetch immediately so the user sees the latest signal status on toggle-on
        fetchSignalUpdate();
      }
      return next;
    });
  };

  // Signal toast notifications (shown in overlay)
  const [signalToasts, setSignalToasts] = useState([]);

  const dismissSignalToast = (id) => {
    setSignalToasts(prev => prev.filter(t => t.id !== id));
  };

  // Helper: get today's date in YYYY-MM-DD format (IST)
  const getTodayIST = () => {
    const now = new Date();
    return now.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }); // "YYYY-MM-DD"
  };

  // Helper: parse "DD Mon YYYY, HH:MM:SS" or ISO-like timestamps from API
  const parseTimestamp = (ts) => {
    if (!ts) return null;
    // Try direct Date parse first
    const d = new Date(ts);
    if (!isNaN(d)) return d;
    // Try format: "24 Apr 2026, 14:15:00"
    const match = ts.match(/(\d{1,2})\s+(\w+)\s+(\d{4}),?\s+(\d{2}):(\d{2}):(\d{2})/);
    if (match) {
      return new Date(`${match[2]} ${match[1]} ${match[3]} ${match[4]}:${match[5]}:${match[6]} GMT+0530`);
    }
    return null;
  };

  // Core fetch: called at HH:15:20 IST
  const fetchSignalUpdate = React.useCallback(async () => {
    const todayIST = getTodayIST();
    const nowIST = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const currentHour = nowIST.getHours(); // e.g. 13 when fetching at 13:15:20
    // Expected candle timestamp hour = currentHour - 1
    const expectedHour = currentHour - 1;
    // Build expected timestamp string pattern: "YYYY-MM-DD THH:15:00" in IST
    // We'll compare using hour only for robustness
    const expectedMinute = 15;

    const endpoints = [
      { label: "V5", url: `https://godseye.server.sypto.xyz/signal_update?start_date=${todayIST}&end_date=${todayIST}&table_name=options_v5_fyers_60min` },
      { label: "V5_2", url: `https://godseye.server.sypto.xyz/signal_update?start_date=${todayIST}&end_date=${todayIST}&table_name=options_v5_2_fyers_60min` },
      { label: "V6", url: `https://godseye.server.sypto.xyz/signal_update?start_date=${todayIST}&end_date=${todayIST}&table_name=options_v6_fyers_60min` },
    ];

    const results = await Promise.all(
      endpoints.map(async ({ label, url }) => {
        try {
          const res = await apiClient.get(url);
          const json = res.data;
          // API may return array or { data: [...] }
          const rows = Array.isArray(json) ? json : (json.data || json.result || []);
          // Look for a row whose time_stamp matches expectedHour:15:00
          const matched = rows.some(row => {
            const ts = row.time_stamp || row.timestamp || row.TimeStamp;
            const d = parseTimestamp(ts);
            if (!d) return false;
            const istD = new Date(d.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
            return istD.getHours() === expectedHour && istD.getMinutes() === expectedMinute;
          });
          return { label, received: matched };
        } catch {
          return { label, received: false, error: true };
        }
      })
    );

    const fetchedAt = new Date().toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false
    });
    const expectedTs = `${String(expectedHour).padStart(2, "0")}:15:00`;

    const id = `signal-${Date.now()}`;
    setSignalToasts([{ id, results, fetchedAt, expectedTs }]);
  }, []);

  // Scheduler: fire at HH:15:20 IST, hours 10–15 (10:15:20 through 15:15:20)
  React.useEffect(() => {
    if (!signalAlertEnabled) return;

    const tick = () => {
      const nowIST = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
      const h = nowIST.getHours();
      const m = nowIST.getMinutes();
      const s = nowIST.getSeconds();
      // Fire from 10:15:20 to 15:15:20 (at HH:15:20)
      if (m === 15 && s === 20 && h >= 10 && h <= 15) {
        fetchSignalUpdate();
      }
    };

    // Sync to next second boundary
    const msToNextSecond = 1000 - (Date.now() % 1000);
    let interval;
    const timeout = setTimeout(() => {
      tick();
      interval = setInterval(tick, 1000);
    }, msToNextSecond);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [signalAlertEnabled, fetchSignalUpdate]);

  // hit backend toggle APIs
  const handleToggleSell = async () => {
    try {
      const res = await apiClient.post(`${server.godseye.baseUrl}optimus/settings/toggle/sell`);
      setSellModule(res.data.sell_module);
    } catch (e) {
      console.error("Toggle sell failed", e);
    }
  };

  const handleToggleExit = async () => {
    try {
      const res = await apiClient.post(`${server.godseye.baseUrl}optimus/settings/toggle/exit`);
      setExitModule(res.data.exit_module);
    } catch (e) {
      console.error("Toggle exit failed", e);
    }
  };

  const handleToggleSellXts = async () => {
    try {
      const res = await apiClient.post("https://optimus.dhanss.server.sypto.xyz/optimus/settings/toggle/sell_xts");
      // API returns { current: "true" | "false" }
      setSellXtsEnabled(res.data.current === "true");
    } catch (e) {
      console.error("Toggle sell_xts failed", e);
    }
  };

  const handleToggleSellXtsAnurag = async () => {
    try {
      const res = await apiClient.post("https://optimus.dhanss.anurag.server.sypto.xyz/optimus/settings/toggle/sell_xts");
      // API returns { current: "true" | "false" }
      setSellXtsAnuragEnabled(res.data.current === "true");
    } catch (e) {
      console.error("Toggle sell_xts_anurag failed", e);
    }
  };

  const handleToggleSellFyers = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/sell_fyers");
      // API returns { current: "true" | "false" }
      setSellFyersEnabled(res.data.current === "true");
    } catch (e) {
      console.error("Toggle sell_fyers failed", e);
    }
  };

  const handleToggleSellDhanHq = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/sell_dhanhq");
      // API returns { current: "true" | "false" }
      setSellDhanHqEnabled(res.data.current === "true");
    } catch (e) {
      console.error("Toggle sell_dhanhq failed", e);
    }
  };

  const handleToggleSellNubra = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/sell_nubra");
      // API returns { current: "true" | "false" }
      setSellNubraEnabled(res.data.current === "true");
    } catch (e) {
      console.error("Toggle sell_nubra failed", e);
    }
  };

  const handleToggleSellPledgePlusDhanHq = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/sell_pledge_plus_dhanhq");
      setSellPledgePlusDhanHqEnabled(res.data.current === "true");
    } catch (e) { console.error("Toggle sell_pledge_plus_dhanhq failed", e); }
  };

  const handleToggleSellPledgePlusXts = async () => {
    try {
      const res = await apiClient.post("https://optimus.dhanss.server.sypto.xyz/optimus/settings/toggle/sell_pledge_plus_dhan_xts");
      setSellPledgePlusXtsEnabled(res.data.current === "true");
    } catch (e) { console.error("Toggle sell_pledge_plus_dhan_xts failed", e); }
  };

  const handleToggleSellPledgePlusXtsAnurag = async () => {
    try {
      const res = await apiClient.post("https://optimus.dhanss.anurag.server.sypto.xyz/optimus/settings/toggle/sell_pledge_plus_dhan_xts");
      setSellPledgePlusXtsAnuragEnabled(res.data.current === "true");
    } catch (e) { console.error("Toggle sell_pledge_plus_dhan_xts (Anurag) failed", e); }
  };

  const handleToggleSellPledgePlusFyers = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/sell_pledge_plus_fyers");
      setSellPledgePlusFyersEnabled(res.data.current === "true");
    } catch (e) { console.error("Toggle sell_pledge_plus_fyers failed", e); }
  };

  const handleToggleSellPledgePlusZerodha = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/sell_pledge_plus_zerodha");
      setSellPledgePlusZerodhaEnabled(res.data.current === "true");
    } catch (e) { console.error("Toggle sell_pledge_plus_zerodha failed", e); }
  };

  const handleTogglePledgeIndexFyers = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/pledge_sl_index_module_fyers");
      setPledgeIndexFyersEnabled(res.data.current === "true");
    } catch (e) { console.error("Toggle pledge_sl_index_module_fyers failed", e); }
  };

  const handleTogglePledgeIndexDhanHq = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/pledge_sl_index_module_dhanhq");
      setPledgeIndexDhanHqEnabled(res.data.current === "true");
    } catch (e) { console.error("Toggle pledge_sl_index_module_dhanhq failed", e); }
  };

  const handleTogglePledgeIndexZerodha = async () => {
    try {
      const res = await apiClient.post("https://godseye.server.sypto.xyz/optimus/settings/toggle/pledge_sl_index_module_zerodha");
      setPledgeIndexZerodhaEnabled(res.data.current === "true");
    } catch (e) { console.error("Toggle pledge_sl_index_module_zerodha failed", e); }
  };
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    module: "", // "sell" or "exit"
    action: "", // "enable" or "disable"
    onConfirm: null, // function to call if user confirms
    loading: false,
  });


  // --- User Access ---
  const [user] = useAuthState(auth);
  // Seed from localStorage immediately so tabs are filtered correctly on first render
  // (avoids the 1-2s flash where all tabs show before the API responds)
  const [userAccess, setUserAccess] = useState(() => {
    try {
      const cached = localStorage.getItem("user_access");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  React.useEffect(() => {
    const fetchAccess = async () => {
      if (user && user.email) {
        try {
          const response = await apiClient.get(`user-access?email=${user.email}`);
          if (response.data.status === "success") {
            setUserAccess(response.data.access);
            localStorage.setItem("user_access", JSON.stringify(response.data.access));
          }
        } catch (error) {
          console.error("Error fetching real-time access (DashboardNew):", error);
          const cached = localStorage.getItem("user_access");
          if (cached) setUserAccess(JSON.parse(cached));
        }
      }
    };

    fetchAccess();
  }, [user]);

  const [optimusSubView, setOptimusSubView] = useState("positions"); // "positions" | "orders"

  const tabs = [
    {
      id: "optimus",
      label: "Optimus",
      count: optimusTotalCount,
      loading: isPositionsInitialLoading || isOrdersInitialLoading,
      permission: "is_optimus_access",
      masterPermission: "is_optimus_access",
    },
    {
      id: "pledge_plus",
      label: "Pledge+",
      count: (commoditiesViewData?.data?.length || 0) + (pledgePlusOrdersViewData?.data?.length || 0),
      loading: isPositionsInitialLoading || isOrdersInitialLoading,
      permission: "is_pledge_plus_access",
      masterPermission: "is_pledge_plus_access",
      subTabs: [
        { id: "commodities", label: "Positions", permission: "is_pledge_plus_positions" },
        { id: "pledge_plus_orders", label: "Orders", permission: "is_pledge_plus_orders" }
      ]
    },
    {
      id: "alpaca",
      label: "Alpaca",
      count: alpacaPositionsCount + alpacaOrdersCount,
      loading: isPositionsInitialLoading || isOrdersInitialLoading,
      permission: "is_alpaca_access",
      masterPermission: "is_alpaca_access",
      subTabs: [
        { id: "alpaca_positions", label: "Positions", permission: "is_alpaca_positions" },
        { id: "alpaca_orders", label: "Orders", permission: "is_alpaca_orders" }
      ]
    },
    {
      id: "rms_arb",
      label: "RMS-Arbitrage",
      count: 0,
      loading: isPositionsInitialLoading,
      subTabs: [
        { id: "rms_arb_positions", label: "Positions" },
        { id: "rms_arb_orders", label: "Orders" }
      ]
    },
    {
      id: "raw",
      label: "Position Manager",
      count: positionsArray.length,
      loading: isPositionsInitialLoading,
      permission: "is_position_manager_tab"
    },
    {
      id: "broker_raw",
      label: "Broker Raw Positions",
      count: rawBrokerPositions.length,
      loading: false,
      permission: "is_broker_raw_position"
    },
    {
      id: "account",
      label: "Account View",
      count: safeUserMappings.length,
      loading: userMappingsLoading,
      permission: "is_account_view"
    },

    {
      id: "rejected",
      label: "Rejected Order View",
      count: rejectedOrdersData.length,
      loading: rejectOrdersLoading,
      permission: "is_rejected_orders"
    },
    {
      id: "exitposition",
      label: "Exit Position View",
      count: positionsArray.length,
      loading: isPositionsInitialLoading,
      permission: "is_exit_position_view"
    },
  ];
  // console.log("NEW UPDATE");
  const filteredTabs = useMemo(() => {
    if (!userAccess) return [];
    return tabs.filter(tab => {
      // 1. Check master permission first (if it exists). If master is false, hide the tab.
      if (tab.masterPermission && userAccess[tab.masterPermission] === false) return false;

      // 2. Then check the granular permission.
      if (tab.permission && userAccess[tab.permission] === false) return false;

      return true;
    }).map(tab => {
      if (tab.subTabs) {
        const allowedSubTabs = tab.subTabs.filter(sub => !sub.permission || userAccess[sub.permission] !== false).map(sub => {
          let count = 0;
          if (sub.id === "optimus_index" || sub.id === "positions" || sub.id === "orders") count = indexOptionsTotalCount;
          if (sub.id === "optimus_stock") count = stockOptionsTotalCount;
          if (sub.id === "optimus_commodity") count = commodityOptionsTotalCount;
          if (sub.id === "commodities") count = commoditiesViewData?.data?.length || 0;
          if (sub.id === "pledge_plus_orders") count = pledgePlusOrdersViewData?.data?.length || 0;
          if (sub.id === "alpaca_positions") count = alpacaPositionsCount;
          if (sub.id === "alpaca_orders") count = alpacaOrdersCount;
          return { ...sub, count };
        });
        // Recalculate count based on allowed subtabs
        let newCount = 0;
        allowedSubTabs.forEach(sub => {
          newCount += sub.count;
        });
        return { ...tab, subTabs: allowedSubTabs, count: newCount };
      }
      return tab;
    }).filter(tab => {
      if (tab.subTabs && tab.subTabs.length === 0) return false;
      return true;
    });
  }, [userAccess, indexOptionsTotalCount, stockOptionsTotalCount, commodityOptionsTotalCount, commoditiesViewData, pledgePlusOrdersViewData, alpacaPositionsCount, alpacaOrdersCount, positionsArray, rawBrokerPositions, safeUserMappings, rejectedOrdersData, isPositionsInitialLoading, isOrdersInitialLoading, userMappingsLoading, rejectOrdersLoading]);

  const [activeTab, setActiveTab] = useState("optimus");
  const [optimusCategory, setOptimusCategory] = useState("index"); // "index" | "stock" | "commodity"

  const activeCategory = useMemo(() => {
    if (activeTab === "optimus_stock" || activeTab === "optimus_stock_positions" || activeTab === "optimus_stock_orders") return "stock";
    if (activeTab === "optimus_commodity" || activeTab === "optimus_commodity_positions" || activeTab === "optimus_commodity_orders") return "commodity";
    if (activeTab === "optimus_index" || activeTab === "optimus_index_positions" || activeTab === "optimus_index_orders" || activeTab === "positions" || activeTab === "orders") return "index";
    return optimusCategory;
  }, [activeTab, optimusCategory]);

  const activeMainTabId = useMemo(() => {
    if (
      activeTab === "optimus" ||
      activeTab === "optimus_index" ||
      activeTab === "optimus_index_positions" ||
      activeTab === "optimus_index_orders" ||
      activeTab === "positions" ||
      activeTab === "orders" ||
      activeTab === "optimus_stock" ||
      activeTab === "optimus_stock_positions" ||
      activeTab === "optimus_stock_orders" ||
      activeTab === "optimus_commodity" ||
      activeTab === "optimus_commodity_positions" ||
      activeTab === "optimus_commodity_orders"
    ) return "optimus";
    if (activeTab === "commodities" || activeTab === "pledge_plus_orders") return "pledge_plus";
    if (activeTab === "alpaca_positions" || activeTab === "alpaca_orders") return "alpaca";
    if (activeTab === "rms_arb_positions" || activeTab === "rms_arb_orders") return "rms_arb";
    return activeTab;
  }, [activeTab]);

  const activeMainTab = useMemo(() => {
    return filteredTabs.find(tab => tab.id === activeMainTabId);
  }, [filteredTabs, activeMainTabId]);

  // Automatic fallback if active category is hidden by admin control
  useEffect(() => {
    if (activeMainTabId === "optimus" && userAccess) {
      const isIndexOk = userAccess.is_optimus_index !== false && userAccess.optimus_index_control !== false;
      const isStockOk = userAccess.is_optimus_stock !== false && userAccess.optimus_stock_control !== false;
      const isCommOk = userAccess.is_optimus_commodity !== false && userAccess.optimus_commodity_control !== false;

      if (activeCategory === "index" && !isIndexOk) {
        if (isStockOk) setOptimusCategory("stock");
        else if (isCommOk) setOptimusCategory("commodity");
      } else if (activeCategory === "stock" && !isStockOk) {
        if (isIndexOk) setOptimusCategory("index");
        else if (isCommOk) setOptimusCategory("commodity");
      } else if (activeCategory === "commodity" && !isCommOk) {
        if (isIndexOk) setOptimusCategory("index");
        else if (isStockOk) setOptimusCategory("stock");
      }
    }
  }, [activeMainTabId, activeCategory, userAccess]);

  React.useEffect(() => {
    if (filteredTabs.length === 0) return;
    // Only reset the active tab if the currently selected tab is no longer visible
    // (e.g., when userAccess loads and removes certain tabs).
    // Do NOT depend on filteredTabs directly — it recomputes on every data tick.
    const isCurrentTabAllowed = filteredTabs.some(t => {
      if (t.id === activeTab) return true;
      if (t.subTabs && t.subTabs.some(sub => sub.id === activeTab)) return true;
      if (t.id === "optimus" && (
        activeTab === "optimus" || activeTab.startsWith("optimus_") || activeTab === "positions" || activeTab === "orders"
      )) return true;
      return false;
    });
    if (!isCurrentTabAllowed) {
      const firstTab = filteredTabs[0];
      if (firstTab.subTabs && firstTab.subTabs.length > 0) {
        setActiveTab(firstTab.subTabs[0].id);
      } else {
        setActiveTab(firstTab.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAccess]);

  // console.log("Position View Data-----",positionViewData);
  // -------------------------------------------------------
  // 7️⃣ RENDER
  // -------------------------------------------------------
  return (
    <div className="min-h-screen bg-[#0c0e15] flex flex-col">
      {/* ------------------------- HEADER ------------------------- */}
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            height: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(55, 65, 81, 0.5);
            border-radius: 9999px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(75, 85, 99, 1);
          }
          header.dashboard-header-override {
            margin-bottom: 0px !important;
            padding-bottom: 0px !important;
            border-bottom: 1px solid rgba(55, 65, 81, 0.4) !important;
          }
        `}
      </style>
      <header className="bg-[#0f1118] sticky top-0 z-40 flex flex-col dashboard-header-override border-b border-gray-800">
        {/* ── Unified tab nav: each column = main tab (top) + subtabs (bottom) ── */}
        <div className="flex items-stretch w-full">
          {/* Scrollable tab list */}
          <div className="flex-1 overflow-x-auto custom-scrollbar">
            <div className="flex items-stretch min-w-max h-full">
              {filteredTabs.map((tab, idx) => {
                const isActive = activeMainTabId === tab.id;
                const isAlertState = tab.id === 'raw' && hasNegativeSell;
                const hasSubTabs = tab.subTabs && tab.subTabs.length > 0;
                return (
                  <React.Fragment key={tab.id}>
                    {/* Vertical separator specifically between first three tabs (separating Optimus | Pledge+ | Alpaca | other tabs) */}
                    {(idx === 1 || idx === 2 || idx === 3) && (
                      <div className="flex flex-col items-center justify-stretch self-stretch px-3 z-10">
                        <div className="w-[1.5px] h-full bg-gray-800/80 rounded-full" />
                      </div>
                    )}
                    {/* Tab column */}
                    <div className="flex flex-col h-full justify-between">
                      {/* ── Main tab button ── */}
                      <button
                        onClick={() => {
                          if (hasSubTabs) {
                            setActiveTab(tab.subTabs[0].id);
                          } else {
                            setActiveTab(tab.id);
                          }
                        }}
                        className={`
                          relative flex items-center justify-center gap-2 w-full px-4 md:px-5 transition-all duration-150 outline-none whitespace-nowrap
                          ${hasSubTabs ? 'h-12 text-[15px] font-medium' : 'h-full flex-1 py-3 text-[16px] font-semibold'}
                          ${isActive
                            ? 'text-white font-bold bg-[#141622]/60'
                            : isAlertState
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-500/5'
                              : 'text-gray-400 hover:text-gray-200 hover:bg-white/[0.03]'
                          }
                        `}
                      >
                        {isAlertState && <AlertCircle className="w-3.5 h-3.5 text-red-500 animate-pulse flex-shrink-0" />}
                        <span>{tab.label}</span>
                        {tab.count > 0 && !hasSubTabs && (
                          <span className={`px-2 py-0.5 rounded-full text-[12px] font-bold leading-none tabular-nums
                            ${isAlertState
                              ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                              : isActive
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'bg-[#141622] text-gray-400 border border-gray-800'
                            }`}>
                            {tab.count}
                          </span>
                        )}
                        {/* Active top-row underline */}
                        {isActive && !hasSubTabs && (
                          <span className={`absolute bottom-0 left-2 right-2 h-[2.5px] rounded-t-full
                            ${isAlertState
                              ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]'
                              : 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]'
                            }`}
                          />
                        )}
                      </button>
                      {/* ── Sub-tab row (rendered ONLY if hasSubTabs is true) ── */}
                      {hasSubTabs && (
                        <div className={`flex items-center justify-center gap-0.5 px-3 h-9 border-t ${
                          isActive
                            ? 'border-gray-800 bg-[#0c0e15]'
                            : 'border-gray-800/60 bg-[#0a0c10]'
                        }`}>
                          {tab.subTabs.map((sub, sIdx) => {
                            const isSubActive = (
                              activeTab === sub.id ||
                              (sub.id === "optimus_index" && (activeTab === "positions" || activeTab === "orders" || activeTab === "optimus_index_positions" || activeTab === "optimus_index_orders")) ||
                              (sub.id === "optimus_stock" && (activeTab === "optimus_stock_positions" || activeTab === "optimus_stock_orders")) ||
                              (sub.id === "optimus_commodity" && (activeTab === "optimus_commodity_positions" || activeTab === "optimus_commodity_orders"))
                            );
                            return (
                              <React.Fragment key={sub.id}>
                                {sIdx > 0 && (
                                  <div className="w-px h-3 bg-gray-800 mx-1 rounded-full flex-shrink-0" />
                                )}
                                <button
                                  onClick={() => setActiveTab(sub.id)}
                                  className={`relative flex items-center gap-1.5 px-2 py-1 text-[12px] font-medium rounded transition-all outline-none whitespace-nowrap
                                    ${isSubActive
                                      ? 'text-blue-400 font-bold bg-blue-500/10'
                                      : 'text-gray-400 hover:text-white'
                                    }`}
                                >
                                  <span>{sub.label}</span>
                                  {sub.count > 0 && (
                                    <span className={`px-1 py-0.5 rounded text-[10px] font-bold leading-none tabular-nums
                                      ${isSubActive
                                        ? 'bg-blue-500/20 text-blue-300'
                                        : 'bg-[#141622] text-gray-500 border border-gray-800'
                                      }`}>
                                      {sub.count}
                                    </span>
                                  )}
                                  {isSubActive && (
                                    <span className="absolute bottom-0 left-1 right-1 h-[2px] bg-blue-500 rounded-t-full shadow-[0_0_5px_rgba(59,130,246,0.5)]" />
                                  )}
                                </button>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          {/* RIGHT SETTINGS ICON - fixed */}
          <div className="relative flex-shrink-0 flex items-center px-3 border-l border-gray-800">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-md transition text-gray-300 hover:text-white ${
                showSettings ? 'bg-[#141622] text-white border border-gray-700' : 'hover:bg-[#141622]'
              }`}
            >
              <Settings className="w-5 h-5" />
            </button>

            {showSettings && (
              <div className="absolute top-full right-0 mt-1 w-64 bg-[#0f1118] border border-gray-700/80 rounded-xl shadow-2xl p-3 z-[999] max-h-[80vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-gray-300 text-sm font-semibold">Optimus Controls</h3>
                  <button
                    onClick={async () => {
                      try {
                        const res = await apiClient.get(`${server.godseye.baseUrl}optimus/settings`);
                        setSellModule(parseInt(res.data.data.sell_module ?? 1));
                        setExitModule(parseInt(res.data.data.exit_module ?? 1));
                      } catch (err) {
                        console.error("Failed to refresh settings", err);
                      }
                      try {
                        const xtsRes = await apiClient.get("https://optimus.dhanss.server.sypto.xyz/optimus/settings/status/sell_xts");
                        setSellXtsEnabled(xtsRes.data.enabled ?? true);
                      } catch (err) {
                        console.error("Failed to refresh sell XTS status", err);
                      }
                      try {
                        const xtsResAnurag = await apiClient.get("https://optimus.dhanss.anurag.server.sypto.xyz/optimus/settings/status/sell_xts");
                        setSellXtsAnuragEnabled(xtsResAnurag.data.enabled ?? true);
                      } catch (err) {
                        console.error("Failed to refresh sell XTS Anurag status", err);
                      }
                      try {
                        const fyersRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_fyers");
                        setSellFyersEnabled(fyersRes.data.enabled ?? true);
                      } catch (err) {
                        console.error("Failed to refresh sell Fyers status", err);
                      }
                      try {
                        const dhanHqRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_dhanhq");
                        setSellDhanHqEnabled(dhanHqRes.data.enabled ?? true);
                      } catch (err) {
                        console.error("Failed to refresh sell DhanHQ status", err);
                      }
                      try {
                        const nubraRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_nubra");
                        setSellNubraEnabled(nubraRes.data.enabled ?? true);
                      } catch (err) {
                        console.error("Failed to refresh sell Nubra status", err);
                      }
                      // Refresh Pledge Plus statuses
                      try {
                        const ppDhanRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_dhanhq");
                        setSellPledgePlusDhanHqEnabled(ppDhanRes.data.enabled ?? true);
                      } catch (err) { console.error("Failed to refresh Pledge Plus DhanHQ status", err); }
                      try {
                        const ppFyersRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_fyers");
                        setSellPledgePlusFyersEnabled(ppFyersRes.data.enabled ?? true);
                      } catch (err) { console.error("Failed to refresh Pledge Plus Fyers status", err); }
                      try {
                        const ppZerodhaRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_zerodha");
                        setSellPledgePlusZerodhaEnabled(ppZerodhaRes.data.enabled ?? true);
                      } catch (err) { console.error("Failed to refresh Pledge Plus Zerodha status", err); }
                      try {
                        const ppxRes = await apiClient.get("https://optimus.dhanss.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_dhan_xts");
                        setSellPledgePlusXtsEnabled(ppxRes.data.enabled ?? true);
                      } catch (err) { console.error("Failed to refresh Pledge Plus XTS status", err); }
                      try {
                        const ppxResAnurag = await apiClient.get("https://optimus.dhanss.anurag.server.sypto.xyz/optimus/settings/status/sell_pledge_plus_dhan_xts");
                        setSellPledgePlusXtsAnuragEnabled(ppxResAnurag.data.enabled ?? true);
                      } catch (err) { console.error("Failed to refresh Pledge Plus XTS Anurag status", err); }
                      // Refresh Pledge+ Index module statuses
                      try {
                        const piiFyersRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/pledge_sl_index_module_fyers");
                        setPledgeIndexFyersEnabled(piiFyersRes.data.enabled ?? true);
                      } catch (err) { console.error("Failed to refresh Pledge Index Fyers status", err); }
                      try {
                        const piiDhanRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/pledge_sl_index_module_dhanhq");
                        setPledgeIndexDhanHqEnabled(piiDhanRes.data.enabled ?? true);
                      } catch (err) { console.error("Failed to refresh Pledge Index DhanHQ status", err); }
                      try {
                        const piiZerodhaRes = await apiClient.get("https://godseye.server.sypto.xyz/optimus/settings/status/pledge_sl_index_module_zerodha");
                        setPledgeIndexZerodhaEnabled(piiZerodhaRes.data.enabled ?? true);
                      } catch (err) { console.error("Failed to refresh Pledge Index Zerodha status", err); }
                    }}
                    className="p-1 rounded-md hover:bg-[#14161f] transition text-gray-300 hover:text-white"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>

                {/* SELL MODULE */}
                {/* <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell",
                      action: sellModule ? "disable" : "enable",
                      onConfirm: handleToggleSell
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>Sell Module</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellModule ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellModule ? "ON" : "OFF"}
                  </span>
                </div> */}

                {/* EXIT MODULE */}
                {/* <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "exit",
                      action: exitModule ? "disable" : "enable",
                      onConfirm: handleToggleExit
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>Exit Module</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${exitModule ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {exitModule ? "ON" : "OFF"}
                  </span>
                </div> */}

                {/* SELL XTS (OPTIMUS) MODULE */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_xts",
                      action: sellXtsEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellXts
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>Sell XTS (Optimus)</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellXtsEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellXtsEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* SELL XTS ANURAG (OPTIMUS) MODULE */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_xts_anurag",
                      action: sellXtsAnuragEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellXtsAnurag
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>Sell XTS Anurag (Optimus)</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellXtsAnuragEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellXtsAnuragEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* SELL FYERS (OPTIMUS) MODULE */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_fyers",
                      action: sellFyersEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellFyers
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>Sell Fyers (Optimus)</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellFyersEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellFyersEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* SELL DHANHQ (OPTIMUS) MODULE */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_dhanhq",
                      action: sellDhanHqEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellDhanHq
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>Sell DhanHQ (Optimus)</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellDhanHqEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellDhanHqEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* SELL NUBRA MODULE */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_nubra",
                      action: sellNubraEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellNubra
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>Sell Nubra (Optimus)</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellNubraEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellNubraEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* PLEDGE PLUS — DHANHQ */}
                <div className="mt-2 px-3 py-1">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Pledge+ Commodities</span>
                </div>
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_pledge_plus_dhan_xts",
                      action: sellPledgePlusXtsEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellPledgePlusXts
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>P+ Sell XTS</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellPledgePlusXtsEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellPledgePlusXtsEnabled ? "ON" : "OFF"}
                  </span>
                </div>
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_pledge_plus_dhan_xts_anurag",
                      action: sellPledgePlusXtsAnuragEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellPledgePlusXtsAnurag
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>P+ Sell XTS Anurag</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellPledgePlusXtsAnuragEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellPledgePlusXtsAnuragEnabled ? "ON" : "OFF"}
                  </span>
                </div>
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_pledge_plus_dhanhq",
                      action: sellPledgePlusDhanHqEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellPledgePlusDhanHq
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>P+ Sell DhanHQ</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellPledgePlusDhanHqEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellPledgePlusDhanHqEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* PLEDGE PLUS — FYERS */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_pledge_plus_fyers",
                      action: sellPledgePlusFyersEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellPledgePlusFyers
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>P+ Sell Fyers</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellPledgePlusFyersEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellPledgePlusFyersEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* PLEDGE PLUS — ZERODHA */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "sell_pledge_plus_zerodha",
                      action: sellPledgePlusZerodhaEnabled ? "disable" : "enable",
                      onConfirm: handleToggleSellPledgePlusZerodha
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>P+ Sell Zerodha</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${sellPledgePlusZerodhaEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {sellPledgePlusZerodhaEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* PLEDGE+ INDEX MODULE */}
                <div className="mt-2 px-3 py-1">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Pledge+ Index</span>
                </div>
                {/* PLEDGE+ INDEX — FYERS */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "pledge_sl_index_module_fyers",
                      action: pledgeIndexFyersEnabled ? "disable" : "enable",
                      onConfirm: handleTogglePledgeIndexFyers
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>P+ Index Fyers</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${pledgeIndexFyersEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {pledgeIndexFyersEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* PLEDGE+ INDEX — DHANHQ */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "pledge_sl_index_module_dhanhq",
                      action: pledgeIndexDhanHqEnabled ? "disable" : "enable",
                      onConfirm: handleTogglePledgeIndexDhanHq
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>P+ Index DhanHQ</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${pledgeIndexDhanHqEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {pledgeIndexDhanHqEnabled ? "ON" : "OFF"}
                  </span>
                </div>

                {/* PLEDGE+ INDEX — ZERODHA */}
                <div
                  onClick={() =>
                    setConfirmModal({
                      show: true,
                      module: "pledge_sl_index_module_zerodha",
                      action: pledgeIndexZerodhaEnabled ? "disable" : "enable",
                      onConfirm: handleTogglePledgeIndexZerodha
                    })
                  }
                  className="flex justify-between items-center px-3 py-2 rounded-md cursor-pointer hover:bg-[#2a2d3a] text-gray-200"
                >
                  <span>P+ Index Zerodha</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${pledgeIndexZerodhaEnabled ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                    {pledgeIndexZerodhaEnabled ? "ON" : "OFF"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

      </header>

      {/* OPTIMUS OPTIONS SUB-VIEW TOOLBAR (Grouped Category Blocks with Direct Positions | Orders Buttons) */}
      {activeMainTabId === "optimus" && (
        <div className="bg-[#141722] border-b border-gray-800/80 px-4 py-2 flex flex-wrap items-center justify-start gap-3 shadow-md shrink-0">
          
          {/* --- Index Options Group --- */}
          {(!userAccess || (userAccess.is_optimus_index !== false && userAccess.optimus_index_control !== false)) && (
            <div className={`flex items-center gap-2 p-1 rounded-xl border transition-all ${
              activeCategory === "index"
                ? "bg-[#181c2b] border-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.15)]"
                : "bg-[#0b0c10] border-gray-800/80 hover:border-gray-700"
            }`}>
              <div className="flex items-center gap-1.5 pl-2.5 pr-1">
                <span className={`text-xs font-bold whitespace-nowrap ${
                  activeCategory === "index" ? "text-blue-400 font-extrabold" : "text-gray-300"
                }`}>
                  Index Options
                </span>
                <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-blue-500/20 text-blue-300 border border-blue-500/30 tabular-nums">
                  {indexOptionsTotalCount}
                </span>
              </div>

              <div className="flex items-center gap-1 bg-black/40 p-0.5 rounded-lg border border-gray-800/60">
                <button
                  onClick={() => {
                    setOptimusCategory("index");
                    setOptimusSubView("positions");
                    setActiveTab("optimus_index");
                  }}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${
                    activeCategory === "index" && optimusSubView === "positions"
                      ? "bg-blue-600 text-white font-bold shadow-md"
                      : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.05]"
                  }`}
                >
                  <span>Positions</span>
                  <span className="px-1 py-0.5 rounded bg-black/40 text-[9px] font-bold tabular-nums">
                    {indexPositionsCount}
                  </span>
                </button>

                <button
                  onClick={() => {
                    setOptimusCategory("index");
                    setOptimusSubView("orders");
                    setActiveTab("optimus_index");
                  }}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${
                    activeCategory === "index" && optimusSubView === "orders"
                      ? "bg-blue-600 text-white font-bold shadow-md"
                      : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.05]"
                  }`}
                >
                  <span>Orders</span>
                  <span className="px-1 py-0.5 rounded bg-black/40 text-[9px] font-bold tabular-nums">
                    {orderViewData.buyOrdersCount || 0}
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* --- Stock Options Group --- */}
          {(!userAccess || (userAccess.is_optimus_stock !== false && userAccess.optimus_stock_control !== false)) && (
            <div className={`flex items-center gap-2 p-1 rounded-xl border transition-all ${
              activeCategory === "stock"
                ? "bg-[#14231e] border-emerald-500/50 shadow-[0_0_12px_rgba(16,185,129,0.15)]"
                : "bg-[#0b0c10] border-gray-800/80 hover:border-gray-700"
            }`}>
              <div className="flex items-center gap-1.5 pl-2.5 pr-1">
                <span className={`text-xs font-bold whitespace-nowrap ${
                  activeCategory === "stock" ? "text-emerald-400 font-extrabold" : "text-gray-300"
                }`}>
                  Stock Options
                </span>
                <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 tabular-nums">
                  {stockOptionsTotalCount}
                </span>
              </div>

              <div className="flex items-center gap-1 bg-black/40 p-0.5 rounded-lg border border-gray-800/60">
                <button
                  onClick={() => {
                    setOptimusCategory("stock");
                    setOptimusSubView("positions");
                    setActiveTab("optimus_stock");
                  }}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${
                    activeCategory === "stock" && optimusSubView === "positions"
                      ? "bg-emerald-600 text-white font-bold shadow-md"
                      : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.05]"
                  }`}
                >
                  <span>Positions</span>
                  <span className="px-1 py-0.5 rounded bg-black/40 text-[9px] font-bold tabular-nums">
                    {stockOptionsPositionsCount}
                  </span>
                </button>

                <button
                  onClick={() => {
                    setOptimusCategory("stock");
                    setOptimusSubView("orders");
                    setActiveTab("optimus_stock");
                  }}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${
                    activeCategory === "stock" && optimusSubView === "orders"
                      ? "bg-emerald-600 text-white font-bold shadow-md"
                      : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.05]"
                  }`}
                >
                  <span>Orders</span>
                  <span className="px-1 py-0.5 rounded bg-black/40 text-[9px] font-bold tabular-nums">
                    {stockOptionsOrdersData.buyOrdersCount || 0}
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* --- Commodity Options Group --- */}
          {(!userAccess || (userAccess.is_optimus_commodity !== false && userAccess.optimus_commodity_control !== false)) && (
            <div className={`flex items-center gap-2 p-1 rounded-xl border transition-all ${
              activeCategory === "commodity"
                ? "bg-[#251b14] border-amber-500/50 shadow-[0_0_12px_rgba(245,158,11,0.15)]"
                : "bg-[#0b0c10] border-gray-800/80 hover:border-gray-700"
            }`}>
              <div className="flex items-center gap-1.5 pl-2.5 pr-1">
                <span className={`text-xs font-bold whitespace-nowrap ${
                  activeCategory === "commodity" ? "text-amber-400 font-extrabold" : "text-gray-300"
                }`}>
                  Commodity Options
                </span>
                <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-amber-500/20 text-amber-300 border border-amber-500/30 tabular-nums">
                  {commodityOptionsTotalCount}
                </span>
              </div>

              <div className="flex items-center gap-1 bg-black/40 p-0.5 rounded-lg border border-gray-800/60">
                <button
                  onClick={() => {
                    setOptimusCategory("commodity");
                    setOptimusSubView("positions");
                    setActiveTab("optimus_commodity");
                  }}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${
                    activeCategory === "commodity" && optimusSubView === "positions"
                      ? "bg-amber-600 text-white font-bold shadow-md"
                      : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.05]"
                  }`}
                >
                  <span>Positions</span>
                  <span className="px-1 py-0.5 rounded bg-black/40 text-[9px] font-bold tabular-nums">
                    {commodityOptionsPositionsCount}
                  </span>
                </button>

                <button
                  onClick={() => {
                    setOptimusCategory("commodity");
                    setOptimusSubView("orders");
                    setActiveTab("optimus_commodity");
                  }}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${
                    activeCategory === "commodity" && optimusSubView === "orders"
                      ? "bg-amber-600 text-white font-bold shadow-md"
                      : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.05]"
                  }`}
                >
                  <span>Orders</span>
                  <span className="px-1 py-0.5 rounded bg-black/40 text-[9px] font-bold tabular-nums">
                    {commodityOptionsOrdersData.buyOrdersCount || 0}
                  </span>
                </button>
              </div>
            </div>
          )}

        </div>
      )}

      {/* GLOBAL CONTROLS TOOLBAR */}
      <div className="w-full bg-[#0f1118] border-b border-gray-800 px-4 py-2 flex items-center justify-between gap-3 shrink-0">

        {/* === MARKET CLOCK + UTILITIES === */}
        <div className="flex items-center gap-3">
          {/* <MarketClock /> */}

          {/* Divider */}
          <div className="w-px h-7 bg-gray-800" />

          {/* <NudgeManager symbolStrategyBaseQty={positionViewData.symbolStrategyBaseQty} activeTab={activeTab} /> */}

          {/* Signal Update Alert Toggle */}
          <button
            onClick={handleToggleSignalAlert}
            title={signalAlertEnabled ? "Signal Update Alert: ON (click to disable)" : "Signal Update Alert: OFF (click to enable)"}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide rounded border transition-all ${signalAlertEnabled
                ? "bg-violet-500/20 text-violet-400 border-violet-500/30 hover:bg-violet-500/30"
                : "bg-[#141622] text-gray-400 border-gray-800 hover:text-gray-200 hover:bg-[#1a1d29]"
              }`}
          >
            <Radio className={`w-3.5 h-3.5 ${signalAlertEnabled ? "text-violet-400 animate-pulse" : "text-gray-400"}`} />
            <span>Signal Update Alert</span>
          </button>

          {/* Gap Scanner Notification Button */}
          <button
            onClick={() => {
              setGapNotification(false);
              setShowGapModal(true);
            }}
            title="Open Gap Scanner"
            className="relative flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide rounded border transition-all bg-[#141622] text-gray-300 border-gray-800 hover:text-white hover:bg-[#1a1d29] hover:border-gray-700"
          >
            <Bell className="w-3.5 h-3.5 text-blue-400" />
            <span>Gap Scanner</span>
            {gapNotification && (
              <span className="absolute -top-1.5 -right-1.5 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
            )}
          </button>
        </div>

        <div className="flex items-center gap-3">
          {/* Bot Status */}
          <div className="flex items-center gap-2 bg-[#141622] px-3 py-1.5 rounded border border-gray-800 hidden md:flex">
            <span className={`w-2 h-2 rounded-full ${wsStatus === "open" ? "bg-emerald-400 animate-pulse" : "bg-red-500"}`}></span>
            <span className="text-gray-300 text-xs font-medium">
              {wsStatus === "open" ? "Bot Active" : "Disconnected"}
            </span>
          </div>

          {/* Actions Group */}
          <div className="flex items-center gap-2 bg-[#141622] rounded-lg p-1 border border-gray-800">
            <button
              onClick={handlePing}
              title="Ping Server"
              className="p-1.5 rounded hover:bg-[#1f2334] text-gray-400 hover:text-white transition-colors border-r border-gray-800"
            >
              <Activity className="w-4 h-4" />
            </button>
            <button
              onClick={refreshAll}
              title="Refresh All Data"
              className="p-1.5 rounded hover:bg-[#1f2334] text-blue-400 hover:text-blue-300 transition-colors border-r border-gray-800"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={handleReconnect}
              title="Reconnect WebSocket"
              className={`p-1.5 rounded hover:bg-[#1f2334] transition-colors ${wsStatus === 'open' ? 'text-gray-400 hover:text-white' : 'text-red-400 hover:text-red-300'}`}
            >
              {wsStatus === 'open' ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      <SignalToastOverlay toasts={signalToasts} onDismiss={dismissSignalToast} />

      {/* ------------------------- CONFIRMATION MODAL ------------------------- */}
      {
        confirmModal.show && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-2">
            <div className="bg-[#1a1d29] rounded-xl p-6 max-w-md w-full text-center shadow-lg animate-fade-in">
              <h3 className="text-white text-lg font-semibold mb-4">
                {confirmModal.action === "disable" ? "Disable" : "Enable"} {
                  ({
                    sell: "Sell Module",
                    exit: "Exit Module",
                    sell_xts: "Sell XTS (Optimus)",
                    sell_xts_anurag: "Sell XTS Anurag (Optimus)",
                    sell_fyers: "Sell Fyers (Optimus)",
                    sell_dhanhq: "Sell DhanHQ (Optimus)",
                    sell_nubra: "Sell Nubra (Optimus)",
                    sell_pledge_plus_dhanhq: "P+ Sell DhanHQ",
                    sell_pledge_plus_dhan_xts: "P+ Sell XTS",
                    sell_pledge_plus_dhan_xts_anurag: "P+ Sell XTS Anurag",
                    sell_pledge_plus_fyers: "P+ Sell Fyers",
                    sell_pledge_plus_zerodha: "P+ Sell Zerodha",
                    pledge_sl_index_module_fyers: "P+ Index Fyers",
                    pledge_sl_index_module_dhanhq: "P+ Index DhanHQ",
                    pledge_sl_index_module_zerodha: "P+ Index Zerodha",
                  }[confirmModal.module] ?? confirmModal.module)
                }?
              </h3>
              <p className="text-gray-400 mb-6">
                Are you sure you want to {confirmModal.action} this module?
              </p>
              <div className="flex flex-col sm:flex-row justify-around gap-2">
                <button
                  onClick={() => setConfirmModal({ ...confirmModal, show: false })}
                  disabled={confirmModal.loading}
                  className="px-4 py-2 rounded-md bg-gray-700 text-gray-200 hover:bg-gray-600 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setConfirmModal(prev => ({ ...prev, loading: true }));
                    if (confirmModal.onConfirm) await confirmModal.onConfirm();
                    setConfirmModal(prev => ({ ...prev, show: false, loading: false }));
                  }}
                  disabled={confirmModal.loading}
                  className="flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-500 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {confirmModal.loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <span>Confirm</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* ------------------------- TAB CONTENT ------------------------- */}
      <div className="flex-1 overflow-y-auto relative">
        {activeMainTabId === "optimus" && (
          activeCategory === "stock" ? (
            optimusSubView === "orders"
              ? <OrdersView {...stockOptionsOrdersData} activeIndexes={stockOptionsOrdersData.activeStocks} loading={isOrdersInitialLoading} clientData={accountViewData.data} category="stock" />
              : <PositionView {...stockOptionsPositionsData} activeIndexes={stockOptionsPositionsData.activeStocks} loading={isPositionsInitialLoading} clientData={accountViewData.data} orders={stockOrders} category="stock" />
          ) : activeCategory === "commodity" ? (
            optimusSubView === "orders"
              ? <OrdersView {...commodityOptionsOrdersData} activeIndexes={commodityOptionsOrdersData.activeCommodities} loading={isOrdersInitialLoading} clientData={accountViewData.data} category="commodity" />
              : <PositionView {...commodityOptionsPositionsData} activeIndexes={commodityOptionsPositionsData.activeCommodities} loading={isPositionsInitialLoading} clientData={accountViewData.data} orders={commodityOrders} category="commodity" />
          ) : (
            optimusSubView === "orders"
              ? <OrdersView {...orderViewData} loading={isOrdersInitialLoading} clientData={accountViewData.data} category="index" />
              : <PositionView {...positionViewData} loading={isPositionsInitialLoading} clientData={accountViewData.data} orders={indexOrders} category="index" />
          )
        )}
        {activeTab === "raw" && <RawPositionsView clientData={accountViewData.data} />}
        {activeTab === "broker_raw" && <BrokerRawPositionsView clientData={accountViewData.data} />}
        {activeTab === "exitposition" && <ExitRawPositionsView clientData={accountViewData.data} />}
        {activeTab === "account" && <AccountView data={accountViewData.data} loading={accountViewData.loading} />}
        {activeTab === "rejected" && <RejectedOrderView rejectedOrders={rejectedOrdersData} clientData={accountViewData.data} loading={rejectOrdersLoading} />}
        {activeTab === "commodities" && <CommoditiesPositionsView {...commoditiesViewData} loading={isPositionsInitialLoading} clientData={accountViewData.data} positions={positionsArray} orders={orders} />}
        {activeTab === "pledge_plus_orders" && <PledgePlusOrdersView {...pledgePlusOrdersViewData} loading={isOrdersInitialLoading} orders={orders} positions={positionsArray} />}
        {activeTab === "alpaca_positions" && <AlpacaPositionsView {...alpacaPositionsViewData} loading={isPositionsInitialLoading} positions={positionsArray} orders={orders} />}
        {activeTab === "alpaca_orders" && <AlpacaOrdersView {...alpacaOrdersViewData} loading={isOrdersInitialLoading} orders={orders} positions={positionsArray} />}
        {activeTab === "rms_arb_positions" && <RmsArbPositionsView users={users} />}
        {activeTab === "rms_arb_orders" && <RmsArbOrdersView />}
        {activeTab === "backtest" && <BacktestView />}
      </div>

      {/* ------------------------- GAP SCANNER MODAL ------------------------- */}
      {showGapModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-[#1a1d29] border border-gray-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col w-full max-w-7xl h-[90vh] relative">
            <div className="flex-1 h-full w-full overflow-hidden">
              <GapPlaybookView clientData={accountViewData.data} isModal={true} onClose={() => setShowGapModal(false)} />
            </div>
          </div>
        </div>
      )}
    </div >
  );

}
